import React, { useState, useRef, useContext } from "react";
import {
  Box,
  IconButton,
  Badge,
  Popover,
  Typography,
  Button,
  List,
  ListItem,
  styled,
  Fade,
  Stack,
  alpha,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  ShoppingCart as ShoppingCartIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { CartContext } from "../../contexts/CartContext";
import { useTranslation } from "react-i18next";

const CartPopover = styled(Popover)(({ theme }) => ({
  "& .MuiPopover-paper": {
    width: 320,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 1.5,
    boxShadow: theme.shadows[8],
    transition: "transform 0.2s ease-in-out",
    
    [theme.breakpoints.down("sm")]: {
      width: "100vw",
      maxWidth: "100%",
      position: "fixed",
      left: "0 !important",
      right: "0 !important",
      bottom: "0",
      top: "auto !important",
      maxHeight: "80vh",
      borderRadius: "16px 16px 0 0",
      transform: "none !important",
      padding: theme.spacing(1.5),
    },
    
    [theme.breakpoints.down(400)]: {
      padding: theme.spacing(1),
    }
  },
  pointerEvents: "auto",
}));

const CartPreview = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "100%",
  right: 0,
  width: 320,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[8],
  borderRadius: theme.shape.borderRadius * 1.5,
  padding: theme.spacing(2),
  display: "none",
  zIndex: 1300,
  transition: "opacity 0.2s ease, transform 0.2s ease",
  opacity: 0,
  transform: "translateY(10px)",
  
  ".cart-icon-wrapper:hover &": {
    display: "block",
    opacity: 1,
    transform: "translateY(0)",
  },
  
  [theme.breakpoints.down("sm")]: {
    display: "none !important", // Disable hover preview on mobile
  },
}));

const CartItemImage = styled("img")(({ theme }) => ({
  width: 60,
  height: 80,
  objectFit: "cover",
  borderRadius: theme.shape.borderRadius,
  boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.1)}`,
  transition: "transform 0.2s ease",
  
  "&:hover": {
    transform: "scale(1.05)",
  },
  
  [theme.breakpoints.down("sm")]: {
    width: 48,
    height: 64,
  },
  
  [theme.breakpoints.down(400)]: {
    width: 40,
    height: 56,
  },
}));

const CartItem = styled(ListItem)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  padding: theme.spacing(2, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: "background-color 0.2s ease",
  
  "&:hover": {
    backgroundColor: alpha(theme.palette.action.hover, 0.5),
  },
  
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5, 0),
    gap: theme.spacing(1.5),
  },
  
  [theme.breakpoints.down(400)]: {
    padding: theme.spacing(1, 0),
    gap: theme.spacing(1),
  }
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  position: "sticky",
  top: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 1,
  padding: theme.spacing(1, 0, 2, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(1),
}));

const CartBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -3,
    top: -3,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
    transition: 'all 0.2s ease',
    
    [theme.breakpoints.down(400)]: {
      fontSize: '0.65rem',
      minWidth: '16px',
      height: '16px',
    },
  },
}));

const RemoveButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.error.main,
    backgroundColor: alpha(theme.palette.error.main, 0.1),
  },
  
  [theme.breakpoints.down(400)]: {
    padding: 2,
  },
}));

const PriceTag = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  transition: 'color 0.2s ease',
  
  '&:hover': {
    color: theme.palette.primary.dark,
  },
}));

const StyledCheckoutButton = styled(Button)(({ theme }) => ({
  height: 48,
  borderRadius: theme.shape.borderRadius * 2,
  fontWeight: 600,
  boxShadow: theme.shadows[2],
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
  
  '&:active': {
    transform: 'translateY(0)',
  },
  
  [theme.breakpoints.down("sm")]: {
    height: 44,
  },
  
  [theme.breakpoints.down(400)]: {
    height: 40,
    fontSize: '0.8125rem',
  },
}));

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, subtotal, totalItems } =
    useContext(CartContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const cartIconRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down(400));

  const handleCartClick = (event) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsOpen(false);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleRemoveItem = (itemId) => {
    removeFromCart(itemId);
  };

  const CartContent = () => (
    <>
      <HeaderRow>
        <Typography variant="h6" fontWeight={600} fontSize={isMobile ? "1rem" : "1.125rem"}>
          {t('topBar.cart.yourCart')} ({cartItems.length})
        </Typography>
        {isOpen && (
          <IconButton size="small" onClick={handleClose} aria-label="close cart" 
              sx={{ 
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: 'text.primary'
                },
                p: isExtraSmall ? 0.5 : 1
              }}>
            <CloseIcon fontSize={isExtraSmall ? "small" : "medium"} />
          </IconButton>
        )}
      </HeaderRow>

      <Box
        sx={{
          overflowY: "auto",
          maxHeight: { 
            xs: "calc(80vh - 160px)", 
            sm: "400px"
          },
          scrollbarWidth: "thin",
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'background.default',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'action.hover',
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: 'text.disabled',
            }
          },
        }}
      >
        {cartItems.length > 0 ? (
          <>
            <List sx={{ py: 0 }}>
              {cartItems.map((item) => (
                <CartItem key={item.id}>
                  <CartItemImage src={item.image} alt={item.title} />
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        fontWeight: 600,
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {item.title}
                      {item.format && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        >
                          ({item.format})
                        </Typography>
                      )}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <PriceTag variant="body2">
                        ${(item.price * item.quantity).toFixed(2)}
                      </PriceTag>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 } }}
                      >
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {t('topBar.cart.qty')}: {item.quantity}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <RemoveButton
                    size="small"
                    onClick={() => handleRemoveItem(item.id)}
                    aria-label="Remove item"
                  >
                    <DeleteIcon fontSize={isExtraSmall ? "small" : "medium"} />
                  </RemoveButton>
                </CartItem>
              ))}
            </List>

            <Box
              sx={{
                mt: 2,
                position: "sticky",
                bottom: 0,
                backgroundColor: "background.paper",
                pt: 2,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Typography 
                variant="subtitle1" 
                fontWeight={600} 
                gutterBottom
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                {t('topBar.cart.subtotal')}: <span style={{ color: theme.palette.primary.main }}>${subtotal.toFixed(2)}</span>
              </Typography>
              <Stack spacing={1}>
                <StyledCheckoutButton
                  variant="contained"
                  color="success"
                  fullWidth
                  component={Link}
                  to="/checkout"
                  state={{ type: "BOOK", items: cartItems }}
                  onClick={handleClose}
                >
                  {t('topBar.cart.checkout')}
                </StyledCheckoutButton>
              </Stack>
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography variant="body1" color="text.secondary">
              {t('topBar.cart.empty')}
            </Typography>
            <Button
              component={Link}
              to="/books"
              variant="contained"
              sx={{ mt: 2 }}
              onClick={handleClose}
            >
              {t('topBar.cart.continueShopping')}
            </Button>
          </Box>
        )}
      </Box>
    </>
  );

  return (
    <Box
      className="cart-icon-wrapper"
      ref={cartIconRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Tooltip title={t('topBar.cart.yourCart')} arrow>
        <IconButton 
          color="inherit" 
          onClick={handleCartClick} 
          aria-label="cart"
          size={isExtraSmall ? "small" : "medium"}
          sx={{ 
            p: isExtraSmall ? 0.5 : 1,
            transition: 'transform 0.2s ease',
            '&:hover': { 
              transform: 'translateY(-2px)',
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
            '&:active': {
              transform: 'translateY(0)',
            }
          }}
        >
          <CartBadge badgeContent={totalItems} color="error">
            <ShoppingCartIcon sx={{ 
              color: theme.palette.mode === 'light' ? "#333" : "#fff",
              fontSize: isExtraSmall ? 20 : 24
            }} />
          </CartBadge>
        </IconButton>
      </Tooltip>

      <Fade in={isHovered && !isOpen} timeout={300}>
        <CartPreview>
          <CartContent />
        </CartPreview>
      </Fade>

      <CartPopover
        id="cart-popover"
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        elevation={8}
      >
        <CartContent />
      </CartPopover>
    </Box>
  );
};

export default Cart;
